import React from "react";
import { Target, Eye, Award, CheckCircle2 } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { Link } from "react-router-dom";

const About = () => {
    const { settings } = useSettings();
    if (!settings) return null;

    return (
        <div data-testid="about-page" className="pt-32 pb-20 bg-bg-base">
            {/* hero strip */}
            <section className="px-6 md:px-12 mb-20">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="text-xs uppercase tracking-[0.4em] text-gold mb-5">About Us</div>
                    <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-tight">
                        The story behind <span className="italic gold-text-gradient">Star Events</span>
                    </h1>
                </div>
            </section>

            {/* Owner profile */}
            <section className="px-6 md:px-12 mb-24">
                <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="relative">
                        <div className="absolute -inset-3 bg-gradient-to-br from-gold/30 to-dark-red/30 rounded-2xl blur-2xl" />
                        <img
                            src={settings.owner_photo_url}
                            alt={settings.owner_name}
                            data-testid="about-owner-photo"
                            className="relative w-full max-w-md mx-auto aspect-[3/4] object-cover rounded-2xl border border-gold/30"
                        />
                    </div>
                    <div>
                        <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Meet Our Founder</div>
                        <h2 className="font-serif text-3xl md:text-5xl text-white mb-5">{settings.owner_name}</h2>
                        <p className="text-white/75 leading-relaxed mb-6">{settings.owner_bio}</p>
                        <div className="glass-card rounded-xl p-5 inline-flex items-center gap-4">
                            <Award className="text-gold" size={28} />
                            <div>
                                <div className="text-3xl font-serif gold-text-gradient font-bold">{settings.experience_years}+</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-white/60">Years Experience</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Story */}
            <section className="px-6 md:px-12 mb-24">
                <div className="max-w-4xl mx-auto">
                    <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4 text-center">Our Story</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-white text-center mb-10">The journey so far</h2>
                    <p className="text-white/75 leading-relaxed text-lg" data-testid="about-text">{settings.about_text}</p>
                </div>
            </section>

            {/* Mission / Vision */}
            <section className="px-6 md:px-12 mb-24">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="glass-card rounded-xl p-9">
                        <Target className="text-gold mb-5" size={32} />
                        <h3 className="font-serif text-2xl text-white mb-4">Our Mission</h3>
                        <p className="text-white/70 leading-relaxed" data-testid="about-mission">{settings.mission}</p>
                    </div>
                    <div className="glass-card rounded-xl p-9">
                        <Eye className="text-gold mb-5" size={32} />
                        <h3 className="font-serif text-2xl text-white mb-4">Our Vision</h3>
                        <p className="text-white/70 leading-relaxed" data-testid="about-vision">{settings.vision}</p>
                    </div>
                </div>
            </section>

            {/* Why choose */}
            <section className="px-6 md:px-12 mb-12">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="text-xs uppercase tracking-[0.4em] text-gold mb-4">Why Choose Us</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white">Built on trust, safety & showmanship</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                            "Certified pyrotechnic crew & venue-approved setups",
                            "Latest cold pyro, sparkulars, CO₂ jets, fog & smoke machines",
                            "Self-service, rental and wholesale models — flexible for every budget",
                            "Pan-India delivery and on-ground execution team",
                            "End-to-end planning, design and on-day show control",
                            "Realistic, on-time pricing — no hidden surprises",
                        ].map((t, i) => (
                            <div key={i} className="flex gap-3 items-start p-5 rounded-lg border border-white/8 bg-bg-surface">
                                <CheckCircle2 className="text-gold shrink-0 mt-0.5" size={20} />
                                <span className="text-white/80">{t}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <div className="text-center mt-12">
                <Link to="/contact" data-testid="about-cta-btn" className="btn-gold inline-flex">Let's Plan Your Event</Link>
            </div>
        </div>
    );
};

export default About;
